import { Component, createRef } from "react";
import { LinkContainer } from "react-router-bootstrap";
import { Input, Spinner } from "reactstrap";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { debounce } from "lodash";

import ManagerContext from "../context/ManagerContext";
import { UtilsService } from "../services/UtilsService";
import { JobProgressIndicator } from "./JobProgressIndicator";

import './ActiveJobs.css';


// interface ActiveJobsProps {
//     activeJobs: Job[];
//     loadingActiveJobs?: boolean;
//     showPagination?: boolean;
//     scrollable?: boolean;
//     showDriverNumberFilter?: boolean;
//     driverNumber?: number;

//     jobSelected?: Job;
//     driverNumberFilterChanged?: number;
// }


export class ActiveJobs extends Component {

    utilsService = new UtilsService();
    activeJobsTable = createRef();
    
    
    constructor(props, context) {
        super(props, context);

        this.state = {
            context: context,
            sortField: 'pickupDistance',
            sortOrder: 1,
        };
    }


    componentDidUpdate(prevProps, prevState) {
        if(prevProps.driverNumber !== this.props.driverNumber) {
            this.setFilters({ driverNumber: this.props?.driverNumber });
        }
    }
    
    
    get viewIsSmallerThanLaptop() {
        return window.innerWidth <= 1200;
    }


    activeJobColumns(jobs) {
        const showProgress = (jobs?.find(job => job.showProgress)) ? true : false; /* Consider showProgress as true if at least one of the jobs has showProgress:true */
        const showRouteCode = (jobs?.find(job => job.showRouteCode)) ? true : false; /* Consider showRouteCode as true if at least one of the jobs has showRouteCode:true */
        const showReplayLink = (jobs?.find(job => job.showReplay)) ? true : false; /* Display an extra column to contain the "Replay" link. Do no add the extra column if none of the jobs has showReplay: true */

        const columns = [
            { 
                field: 'routeCode', header: 'Route Code', sortable: true, hidden: showRouteCode ? false : true, filter: true, 
                body: (row) => <span title={row.routeCode}>{row.routeCode}</span> 
            },
            {
                field: 'dropNumber', header: 'Drop Number', sortable: true, hidden: showRouteCode ? false : true,
                body: (row) => <span title={row.dropNumber}>{row.dropNumber}</span>
            },
            { 
                field: 'driverNumber', header: 'Driver', width: '60px', filter: this.props?.showDriverNumberFilter,
                filterElement: <Input innerRef={(e) => this.driverFilterInput = e} onKeyDown={(e) => this.selectDriver(e)} onChange={(e) => this.filterByDriverNumber(e.target?.value)} />,
                body: (row) => <span title={row.driverNumber}>{row.driverNumber}</span>
            },
            {
                field: 'driverDetail.driverName', header: 'Driver Name', sortable: true,
                body: (row) => <span title={row.driverDetail?.driverName}>{row.driverDetail?.driverName}</span>
            },
            {
                field: 'stateAbbrev', header: 'State', width: '40px',
                body: (row) => <span title={row.stateAbbrev}>{row.stateAbbrev}</span>
            },
            { 
                field: 'ref1', header: 'Ref 1', sortable: true, filter: true,
                body: (row) => <span title={row.ref1}>{row.ref1}</span>
            },
            { 
                field: 'ref2', header: 'Ref 2', sortable: true, filter: true,
                body: (row) => <span title={row.ref2}>{row.ref2}</span>
            },
            { 
                field: 'shortJobNumber', header: 'Job Number', sortable: true, width: '55px',
                body: (row) => <span title={row.shortJobNumber}>{row.shortJobNumber}</span>
            },
            { 
                field: 'serviceCode', header: 'Service Code', sortable: true, width: '50px',
                body: (row) => <span title={row.serviceCode}>{row.serviceCode}</span>
            },
            { 
                field: 'receiverName', header: 'Customer Name', sortable: true,
                body: (row) => <span title={row.receiverName}>{row.receiverName}</span>
            },
            { 
                field: 'deliverySuburb', header: 'Deliveries', sortable: true, filter: true,
                body: (row) => <span title={row.deliverySuburb}>{row.deliverySuburb}</span>
            },
            {
                field: 'status', header: 'Status', sortable: true, width: '70px',
                body: (row) => <span title={row.status}>{row.status}</span>
            },
            { 
                field: 'pickupDistance', header: 'KM to P/U', sortable: true, width: '45px',
                body: (row) => {
                    const value = (row.pickupDistance === 9999) ? null : row.pickupDistance;
                    return <span title={value}>{(value)}</span>
                }
            },
            {
                field: 'deliveryDistance', header: 'KM to Del', sortable: true, width: '45px',
                body: (row) => {
                    const value = (row.deliveryDistance === 9999) ? null : row.deliveryDistance;
                    return <span title={value}>{value}</span>
                }
            },
            {
                field: 'etd', header: 'ETD (mins)', sortable: true, width: '40px',
                body: (row) => <span title={row.etd}>{row.etd}</span>
            },
            { 
                field: 'progress', header: 'Estimated Progress',
                hidden: showProgress ? false : true,
                body: (row, column) => {
                  if(row.showEstimatedProgress) {
                    return <JobProgressIndicator progress={row.estimatedProgress ?? 0}/>
                  }  
                }
            },
            { 
                field: '', header: '', 
                hidden: showReplayLink ? false : true,
                body: (row, column) => {
                    if(row.showReplay) {
                        return <div onClick={(e) => e.stopPropagation()}>
                            <LinkContainer to={'/historical-replay/' + row.clientCode + '/' + row.driverNumber + '/' + row.stateAbbrev + '/' + row.modified + '/' + row.jobNumber + '/' + row.subJobNumber}>
                                <Button label="Replay" className="p-button-link text-xs" />
                            </LinkContainer>
                        </div>
                    }
                }
            }
        ];

        return columns;
    }


    /**
     * @param {*} filterValues { driverNumber: number; }
     */
    setFilters(filterValues) {
        this.setState(
            { driverNumber: filterValues.driverNumber },
            () => {
                this.activeJobsTable.filter(filterValues.driverNumber, 'driverNumber');
                this.driverFilterInput.value = filterValues.driverNumber;
            }
        );
    }


    /**
     * Emit "jobSelected" event and pass the driver details.
     * Execute only when the user pressed Enter key while inside the "Driver" column's text filter.
     * If there are multiple drivers in the filtered results, we pick the first driver and pass that in the "jobSelected" event.
     */
    selectDriver(keydownEvent) {
        // Execute only on pressing "Enter" key
        if(keydownEvent.keyCode === 13) { 
            const filters = this.activeJobsTable.getFilters();
            const results = this.activeJobsTable?.filterLocal(this.props?.activeJobs, filters);
            const sortedResults = this.activeJobsTable?.sortSingle(results, 'driverNumber', 1);
            const job = sortedResults[0];

            if(job) {
                this.props?.jobSelected(job);
            }
        }
    }


    /**
     * - Driver column is using custom filter function.
     * - Run this filter whenever the user typed into the Driver column filter.
     * - Also sort the active jobs by Driver column at the same time.
     * - Emit "driverNumberFilterChanged" event so that the parent component is aware that the filter changes.
     * - Sort back the active jobs by pickupDistance after clearing the driver number filter
     */
    filterByDriverNumber(filterValue) {
        this.activeJobsTable.filter(filterValue, 'driverNumber', 'contains');
        this.activeJobsTable.setState({ sortField: 'driverNumber', sortOrder: 1 });
        this.setState({ sortField: 'driverNumber', sortOrder: 1 }); 
        this.emitDriverNumberFilterChanged(filterValue);

        if(!filterValue) {
            this.activeJobsTable.setState({ sortField: 'pickupDistance', sortOrder: -1 });
            this.setState({ sortField: 'pickupDistance', sortOrder: -1 }); 
        }
    }


    emitDriverNumberFilterChanged = debounce(
        (filterValue) => {
            if(this.props?.showDriverNumberFilter && this.props?.driverNumberFilterChanged) {
                return this.props.driverNumberFilterChanged(filterValue);
            }
        }, 250
    );


    renderColumns(columns) {
        const filteredColumns = columns?.filter(column => !column.hidden); /* Do not render columns with "hidden" flag */

        return filteredColumns?.map((column, index) => {
            return <Column 
                key={index} 
                field={column.field} 
                header={column.header} 
                filter={column.filter}
                filterElement={column.filterElement}
                filterMatchMode={'contains'}
                sortable={column.sortable} 
                body={column.body}
                style={{ width: column.width }}
            ></Column>
        });
    }


    renderTable() {
        if(this.props?.loadingActiveJobs) {
            return <div className="flex flex-row align-items-center gap-2 p-2">
                <Spinner color="info" />
                <em>Please wait while we retrieve your current active jobs.</em>
            </div>
        } else if(this.props?.activeJobs?.length === 0) {
            return <p><em>No active jobs. Only jobs allocated to a driver will show here.</em></p>;
        } else {
            return this.renderActiveJobsTable();
        }
    }


    renderActiveJobsTable() {
        const activeJobs = this.props?.activeJobs ?? [];
        const activeJobsTableColumns = this.activeJobColumns(activeJobs);
        
        return <DataTable
            ref={(e) => this.activeJobsTable = e}
            value={activeJobs} 
            className="p-datatable-sm p-datatable-gridlines p-datatable-striped" 
            paginator={this.props?.showPagination ?? true} 
            rows={32}
            rowsPerPageOptions={[32, 50, 100]}
            sortField={this.state.sortField}
            sortOrder={this.state.sortOrder}
            onRowClick={(e) => this.utilsService.openJobTrackingPageUsingClientCodeAndState(e.data.jobNumber, e.data.clientCode, e.data.stateAbbrev, this.context.User, e.data.subJobNumber)}
            autoLayout={true}
            scrollable={this.props?.scrollable ?? false}
            scrollHeight={'calc(100vh - 325px)'}
        >
            {this.renderColumns(activeJobsTableColumns)}
        </DataTable>
    }


    render() {
        return <div className="active-jobs">
            {this.renderTable()}
        </div>
    }

}



ActiveJobs.contextType = ManagerContext;